// 📂 frontend/src/components/UserManager.tsx
import React, { useState } from 'react';
import { useToast } from '../hooks/useToast';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'user' | 'admin';
  theme?: string;
  createdAt: Date;
  lastLogin?: Date | null;
  isActive: boolean;
  emailsSent: number;
  templatesCreated: number;
  contactsCount?: number;
}

interface UserManagerProps {
  users: User[];
  currentUserId: number;
  onUserCreate: (userData: Record<string, unknown>) => void;
  onUserUpdate: (id: number, userData: Record<string, unknown>) => void;
  onUserDelete: (id: number) => void;
  onUserToggle: (id: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any; // ThemeConfig from ThemeContext
  currentTheme: string;
}

const UserManager: React.FC<UserManagerProps> = ({
  users,
  currentUserId,
  onUserCreate,
  onUserUpdate,
  onUserDelete,
  onUserToggle,
  theme,
  currentTheme
}) => {
  const { toast } = useToast();
  const { confirmState, confirm, cancelConfirm } = useConfirm();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'user'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'lastLogin' | 'emailsSent'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin',
    isActive: true
  });

  // Stats
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.isActive).length,
    adminUsers: users.filter(u => u.role === 'admin').length,
    totalEmailsSent: users.reduce((sum, u) => sum + u.emailsSent, 0)
  };

  // Resetare formular
  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      isActive: true
    });
    setEditingUser(null);
  };

  // Populare formular pentru editare
  React.useEffect(() => {
    if (editingUser) {
      setFormData({
        username: editingUser.username,
        email: editingUser.email,
        password: '', // Nu afișăm parola existentă
        role: editingUser.role,
        isActive: editingUser.isActive
      });
      setShowCreateModal(true);
    }
  }, [editingUser]);

  // Salvare utilizator
  const handleSave = async () => {
    if (!formData.username || !formData.email) {
      toast.error('Eroare', 'Numele și email-ul sunt obligatorii!');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Eroare', 'Parola este obligatorie pentru utilizatori noi!');
      return;
    }

    try {
      if (editingUser) {
        await onUserUpdate(editingUser.id, formData);
      } else {
        await onUserCreate(formData);
      }
      
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  // Filtrare și sortare utilizatori
  const getFilteredUsers = () => {
    let filtered = users;

    // Căutare
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrare după rol
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    // Filtrare după status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => 
        filterStatus === 'active' ? user.isActive : !user.isActive
      );
    }

    // Sortare
    filtered.sort((a, b) => {
      let aVal: string | number, bVal: string | number;
      
      switch (sortBy) {
        case 'name':
          aVal = a.username.toLowerCase();
          bVal = b.username.toLowerCase();
          break;
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'lastLogin':
          aVal = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
          bVal = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
          break;
        case 'emailsSent':
          aVal = a.emailsSent;
          bVal = b.emailsSent;
          break;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  // Toggle selection
  const toggleUserSelection = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select all
  const toggleSelectAll = () => {
    const filtered = getFilteredUsers();
    if (selectedUsers.length === filtered.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filtered.map(u => u.id));
    }
  };

  // Bulk actions
  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedUsers.length === 0) {
      toast.warning('Atenție', 'Selectează cel puțin un utilizator');
      return;
    }

    const message = action === 'delete'
      ? `Ștergi ${selectedUsers.length} utilizatori?`
      : `${action === 'activate' ? 'Activezi' : 'Dezactivezi'} ${selectedUsers.length} utilizatori?`;

    const confirmed = await confirm({
      title: 'Confirmare acțiune',
      message,
      variant: action === 'delete' ? 'danger' : 'warning',
      confirmText: action === 'delete' ? 'Șterge' : 'Confirmă',
      cancelText: 'Anulează'
    });
    if (confirmed) {
      selectedUsers.forEach(userId => {
        if (action === 'delete') {
          onUserDelete(userId);
        } else {
          const user = users.find(u => u.id === userId);
          if (user && user.isActive !== (action === 'activate')) {
            onUserToggle(userId);
          }
        }
      });
      setSelectedUsers([]);
      setShowBulkActions(false);
    }
  };

  // Format date
  const formatDate = (date: Date | null | undefined) => {
    if (!date) return 'Niciodată';
    return new Date(date).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export users
  const exportUsers = () => {
    const data = users.map(u => ({
      username: u.username,
      email: u.email,
      role: u.role,
      status: u.isActive ? 'activ' : 'inactiv',
      emailsSent: u.emailsSent,
      templatesCreated: u.templatesCreated,
      createdAt: formatDate(u.createdAt),
      lastLogin: formatDate(u.lastLogin)
    }));

    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('Export', `${users.length} utilizatori exportați!`);
  };

  return (
    <div>
      {/* Header cu statistici */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 16px 0' }}>👥 Gestionare Utilizatori</h2>
        
        {/* Statistici rapide */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '16px',
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.medium
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary }}>
              {stats.totalUsers}
            </div>
            <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
              Total Utilizatori
            </div>
          </div>
          <div style={{
            padding: '16px',
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.medium
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.success }}>
              {stats.activeUsers}
            </div>
            <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
              Utilizatori Activi
            </div>
          </div>
          <div style={{
            padding: '16px',
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.medium
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.accent }}>
              {stats.adminUsers}
            </div>
            <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
              Administratori
            </div>
          </div>
          <div style={{
            padding: '16px',
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.medium
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.warning }}>
              {stats.totalEmailsSent}
            </div>
            <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
              Total Email-uri Trimise
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {/* Căutare */}
          <input
            type="text"
            placeholder="🔍 Caută utilizatori..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: '1 1 300px',
              padding: '8px 12px',
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              color: theme.colors.text
            }}
          />

          {/* Filtre */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as 'all' | 'admin' | 'user')}
            style={{
              padding: '8px 12px',
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              color: theme.colors.text
            }}
          >
            <option value="all">Toate rolurile</option>
            <option value="admin">Administratori</option>
            <option value="user">Utilizatori</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
            style={{
              padding: '8px 12px',
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              color: theme.colors.text
            }}
          >
            <option value="all">Toți</option>
            <option value="active">Activi</option>
            <option value="inactive">Inactivi</option>
          </select>

          {/* Sortare */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'email' | 'lastLogin' | 'emailsSent')}
            style={{
              padding: '8px 12px',
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              color: theme.colors.text
            }}
          >
            <option value="name">Sortează după nume</option>
            <option value="email">Sortează după email</option>
            <option value="lastLogin">Sortează după ultima logare</option>
            <option value="emailsSent">Sortează după email-uri trimise</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            style={{
              padding: '8px 12px',
              backgroundColor: 'transparent',
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              cursor: 'pointer'
            }}
            title={sortOrder === 'asc' ? 'Crescător' : 'Descrescător'}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>

          {/* Acțiuni */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            {selectedUsers.length > 0 && (
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: theme.colors.warning,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                Acțiuni ({selectedUsers.length})
              </button>
            )}
            <button
              onClick={exportUsers}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                cursor: 'pointer'
              }}
            >
              📤 Export
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: theme.colors.primary,
                color: '#ffffff',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                cursor: 'pointer'
              }}
            >
              ➕ Utilizator Nou
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {showBulkActions && selectedUsers.length > 0 && (
          <div style={{
            padding: '12px',
            backgroundColor: theme.colors.warning + '22',
            border: `1px solid ${theme.colors.warning}44`,
            borderRadius: theme.borderRadius.medium,
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '12px' }}>
              {selectedUsers.length} utilizatori selectați
            </span>
            <button
              onClick={() => handleBulkAction('activate')}
              style={{
                padding: '6px 12px',
                backgroundColor: theme.colors.success,
                color: '#ffffff',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Activează
            </button>
            <button
              onClick={() => handleBulkAction('deactivate')}
              style={{
                padding: '6px 12px',
                backgroundColor: theme.colors.warning,
                color: '#ffffff',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Dezactivează
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              style={{
                padding: '6px 12px',
                backgroundColor: theme.colors.danger,
                color: '#ffffff',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Șterge
            </button>
            <button
              onClick={() => {
                setSelectedUsers([]);
                setShowBulkActions(false);
              }}
              style={{
                marginLeft: 'auto',
                padding: '6px 12px',
                backgroundColor: 'transparent',
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.small,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Anulează
            </button>
          </div>
        )}
      </div>

      {/* Tabel utilizatori */}
      <div style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius.large,
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: theme.colors.background }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>
                <input
                  type="checkbox"
                  checked={selectedUsers.length === getFilteredUsers().length && selectedUsers.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Utilizator</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Rol</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Activitate</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Ultima logare</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredUsers().map(user => (
              <tr 
                key={user.id} 
                style={{ 
                  borderTop: `1px solid ${theme.colors.border}`,
                  backgroundColor: selectedUsers.includes(user.id) ? theme.colors.primary + '11' : 'transparent',
                  transition: 'background-color 0.2s'
                }}
              >
                <td style={{ padding: '12px' }}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleUserSelection(user.id)}
                    disabled={user.id === currentUserId}
                  />
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: theme.colors.primary + '22',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '500' }}>
                        {user.username}
                        {user.id === currentUserId && (
                          <span style={{ 
                            marginLeft: '8px',
                            padding: '2px 6px',
                            backgroundColor: theme.colors.primary + '22',
                            color: theme.colors.primary,
                            borderRadius: '12px',
                            fontSize: '11px'
                          }}>
                            Tu
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                        Creat: {formatDate(user.createdAt)}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px', fontSize: '14px' }}>{user.email}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: user.role === 'admin' ? theme.colors.accent + '22' : theme.colors.primary + '22',
                    color: user.role === 'admin' ? theme.colors.accent : theme.colors.primary,
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {user.role === 'admin' ? '👑 Admin' : '👤 User'}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: user.isActive ? theme.colors.success + '22' : theme.colors.danger + '22',
                    color: user.isActive ? theme.colors.success : theme.colors.danger,
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {user.isActive ? '✅ Activ' : '❌ Inactiv'}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ fontSize: '12px' }}>
                    <div>📧 {user.emailsSent} email-uri</div>
                    <div>📝 {user.templatesCreated} template-uri</div>
                  </div>
                </td>
                <td style={{ padding: '12px', fontSize: '14px' }}>
                  {formatDate(user.lastLogin)}
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setEditingUser(user)}
                      disabled={user.id === currentUserId}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: theme.colors.primary + '22',
                        color: theme.colors.primary,
                        border: 'none',
                        borderRadius: theme.borderRadius.small,
                        cursor: user.id === currentUserId ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        opacity: user.id === currentUserId ? 0.5 : 1
                      }}
                      title="Editează"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => onUserToggle(user.id)}
                      disabled={user.id === currentUserId}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: user.isActive ? theme.colors.warning + '22' : theme.colors.success + '22',
                        color: user.isActive ? theme.colors.warning : theme.colors.success,
                        border: 'none',
                        borderRadius: theme.borderRadius.small,
                        cursor: user.id === currentUserId ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        opacity: user.id === currentUserId ? 0.5 : 1
                      }}
                      title={user.isActive ? 'Dezactivează' : 'Activează'}
                    >
                      {user.isActive ? '🔒' : '🔓'}
                    </button>
                    <button
                      onClick={async () => {
                        const confirmed = await confirm({
                          title: 'Confirmare ștergere',
                          message: `Ștergi utilizatorul "${user.username}"?`,
                          variant: 'danger',
                          confirmText: 'Șterge',
                          cancelText: 'Anulează'
                        });
                        if (confirmed) {
                          onUserDelete(user.id);
                        }
                      }}
                      disabled={user.id === currentUserId}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: theme.colors.danger + '22',
                        color: theme.colors.danger,
                        border: 'none',
                        borderRadius: theme.borderRadius.small,
                        cursor: user.id === currentUserId ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        opacity: user.id === currentUserId ? 0.5 : 1
                      }}
                      title="Șterge"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {getFilteredUsers().length === 0 && (
          <div style={{ 
            padding: '60px',
            textAlign: 'center',
            color: theme.colors.textSecondary
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <h3>Nu există utilizatori</h3>
            <p>
              {searchTerm ? 'Nu am găsit utilizatori care să corespundă căutării.' : 'Creează primul utilizator!'}
            </p>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />

      {/* Modal Creare/Editare */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => {
          setShowCreateModal(false);
          resetForm();
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.large,
            padding: '30px',
            maxWidth: '500px',
            width: '100%'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px' }}>
              {editingUser ? '✏️ Editează Utilizator' : '➕ Utilizator Nou'}
            </h2>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Nume utilizator *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="ex: ion.popescu"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ex: ion.popescu@example.com"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Parolă {editingUser ? '(lasă gol pentru a păstra)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? 'Parolă nouă (opțional)' : 'Minim 6 caractere'}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Rol
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'admin' })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                >
                  <option value="user">👤 Utilizator</option>
                  <option value="admin">👑 Administrator</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Cont activ</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                Anulează
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.colors.primary,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                {editingUser ? 'Actualizează' : 'Creează'} Utilizator
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;